import React, { useEffect, useState } from "react";
import { AdminCompanyExtended, AdminOffer } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  DetailRow,
  Section,
  Table,
  SortConfig,
  SortDirection,
  sortData,
} from "../AdminUI";

interface Props {
  company: AdminCompanyExtended;
  onSelectOffer: (o: AdminOffer) => void;
  cachedOffers: AdminOffer[] | undefined;
  onCache: (companyId: string, data: AdminOffer[]) => void;
  refreshKey: number;
  onSelectSalesRep?: (repId: number) => void;
}

export default function CompanyDetails({
  company,
  onSelectOffer,
  cachedOffers,
  onCache,
  refreshKey,
  onSelectSalesRep,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState<AdminOffer[]>(cachedOffers || []);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedOffers = React.useMemo(() => {
    return sortData(offers, sortConfig);
  }, [offers, sortConfig]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (cachedOffers && refreshKey === 0) {
          setOffers(cachedOffers);
          setLoading(false);
          return;
        }
        setLoading(true);
        const data = await adminService.fetchCompanyOffers(company.id);
        if (!mounted) return;
        setOffers(data || []);
        onCache(company.id, data || []);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [company.id, refreshKey]);

  const cols: Column<AdminOffer>[] = [
    {
      key: "position",
      header: "Oferta",
      sortable: true,
      render: (r) => (
        <span className="font-medium text-slate-800">{r.position}</span>
      ),
    },
    {
      key: "location",
      header: "Ubicación",
      sortable: true,
      render: (r) =>
        r.location && r.location !== "0" ? (
          r.location
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "number_of_vacancies",
      header: "Vac.",
      sortable: true,
      render: (r) => r.number_of_vacancies ?? 1,
    },
    {
      key: "created_at",
      header: "Creada",
      sortable: true,
      render: (r) =>
        r.created_at ? (
          <span className="table-date">
            {new Date(r.created_at).toLocaleDateString()}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "end_date",
      header: "Fin",
      sortable: true,
      render: (r) =>
        r.end_date ? (
          <span className="table-date">
            {new Date(r.end_date).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: "status",
      header: "Estado",
      sortable: true,
      render: (r) => {
        const s = r.status.toLowerCase();
        let badgeClass = "";
        let label = r.status;

        if (s === "publish") {
          badgeClass = "status-publish";
          label = "Publicada";
        } else if (s === "draft") {
          badgeClass = "status-draft";
          label = "Borrador";
        } else if (s === "closed" || s === "expired") {
          badgeClass = "status-closed";
          label = "Cerrada";
        }
        return <span className={`badge ${badgeClass}`}>{label}</span>;
      },
    },
    {
      key: "apply_count",
      header: "Cands.",
      sortable: true,
      render: (r) => (
        <span className="font-bold text-slate-700">{r.apply_count || 0}</span>
      ),
    },
  ];

  const handleSetPassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setPasswordError("Debes completar ambos campos de contraseña.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setPasswordSaving(true);
      await adminService.setCompanyPassword(company.id, newPassword);
      setPasswordSuccess("Contraseña actualizada correctamente.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo actualizar la contraseña.";
      setPasswordError(String(message));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <section className="panel">
      {/* Header */}
      <div className="company-details">
        <div className="company-header">
          {company.logo ? (
            <img
              src={company.logo}
              alt="Logo"
              className="company-logo"
              style={{
                maxWidth: "50px",
                maxHeight: "50px",
                objectFit: "contain",
              }}
            />
          ) : (
            <div
              className="company-logo"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "9px",
                color: "#9ca3af",
                fontWeight: "bold",
              }}
            >
              N/A
            </div>
          )}

          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h2 className="company-name">{company.name}</h2>
              <span
                className="badge"
                style={{
                  background: company.is_blocked ? "#fef2f2" : "#ecfdf5",
                  color: company.is_blocked ? "#991b1b" : "#065f46",
                  borderColor: "transparent",
                }}
              >
                {company.is_blocked ? "BLOQUEADO" : "ACTIVO"}
              </span>
            </div>
            <p
              className="company-desc"
              style={{ fontSize: "12px", marginTop: "2px" }}
            >
              ID: {company.id}
            </p>
          </div>
        </div>

        {company.description && (
          <p
            className="company-meta"
            style={{ fontStyle: "italic", marginBottom: "20px" }}
          >
            "{company.description}"
          </p>
        )}
      </div>

      {/* Details */}
      <div className="details-grid">
        <div>
          <Section title="Datos Corporativos y Ubicación">
            <DetailRow label="Razón Social" value={company.business_name} />
            <DetailRow label="NIF" value={company.nif} />
            <DetailRow label="Sector" value={company.sector} />
            <DetailRow label="Web" value={company.url} />
            <DetailRow label="Dirección" value={company.address} />
            <DetailRow label="Ciudad" value={company.city} />
            <DetailRow label="País" value={company.country} />
            <DetailRow label="Creado el" value={company.created_at} />
          </Section>

          <Section title="Plan y Facturación">
            <DetailRow label="Plan" value={company.plan?.name} />
            <DetailRow
              label="Precio"
              value={
                company.plan
                  ? `€${company.plan.price} (+${company.plan.tax}%)`
                  : null
              }
            />
            <DetailRow
              label="Límites"
              value={
                company.plan
                  ? `${company.plan.num_offer} ofertas / ${company.plan.applicants} cands.`
                  : null
              }
            />
            <DetailRow label="Fin Prueba" value={company.trial_ends_at} />
          </Section>
        </div>

        <div>
          <Section title="Usuario">
            <DetailRow
              label="Nombre"
              value={`${company.first_name || ""} ${company.last_name || ""}`}
            />
            <DetailRow label="Email" value={company.email} />
            <DetailRow label="Teléfono" value={company.phone} />
            <DetailRow label="Último Acceso" value={company.last_login} />
            <DetailRow
              label="Proveedor"
              value={company.social_provider || "Local"}
            />

            <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <input
                type="password"
                className="input"
                placeholder="Nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                type="password"
                className="input"
                placeholder="Confirmar contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <div>
                <button
                  className="action-btn solid"
                  onClick={handleSetPassword}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? "Actualizando..." : "Actualizar contraseña"}
                </button>
              </div>
              {passwordError && (
                <span style={{ color: "#b91c1c", fontSize: "12px" }}>
                  {passwordError}
                </span>
              )}
              {passwordSuccess && (
                <span style={{ color: "#166534", fontSize: "12px" }}>
                  {passwordSuccess}
                </span>
              )}
            </div>
          </Section>

          <Section title="Estado y API">
            <DetailRow label="Verificado" value={company.is_verified} isBool />
            <DetailRow
              label="Términos"
              value={company.is_accepted_terms}
              isBool
            />
            <DetailRow
              label="Publi."
              value={company.is_accepted_commercial}
              isBool
            />
            {company.referral_info ? (
              <div className="detail-row">
                <span className="detail-label">Referido por</span>
                <button
                  className="detail-value"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#2563eb",
                    cursor: "pointer",
                    textDecoration: "underline",
                    font: "inherit",
                    textAlign: "left",
                  }}
                  onClick={() => onSelectSalesRep?.(company.referral_info!.sales_rep_id)}
                  title={`Ver perfil de ${company.referral_info.sales_rep_name}`}
                >
                  {company.referral_info.sales_rep_name}
                </button>
              </div>
            ) : (
              <DetailRow label="Referido" value={company.is_referred_by} isBool />
            )}
            <DetailRow label="Es Origen" value={company.is_origin} isBool />
            <DetailRow label="Bloqueado" value={company.is_blocked} isBool />

            <div
              style={{ margin: "8px 0", borderTop: "1px dashed #e5e7eb" }}
            ></div>

            <DetailRow
              label="GPT-4 Usados"
              value={`${company.request_counter?.gpt_4_requests || 0} / ${
                company.request_counter?.total_requests || 0
              }`}
            />
            <DetailRow
              label="Expira API"
              value={company.request_counter?.counter_expires_at}
            />
          </Section>
        </div>
      </div>

      <hr className="divider" />

      {/* Offers Table */}
      <h3 className="panel-title" style={{ marginBottom: "16px" }}>
        Ofertas de {company.name}
      </h3>
      {loading ? (
        <div className="empty">Cargando ofertas…</div>
      ) : error ? (
        <div className="empty">Error: {error}</div>
      ) : sortedOffers.length ? (
        <Table
          columns={cols}
          rows={sortedOffers}
          keyField="id"
          onRowClick={onSelectOffer}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      ) : (
        <div className="empty">No hay ofertas para esta empresa.</div>
      )}
    </section>
  );
}
